// Phase 9 R2 — Inventory items list query.
// Server-side filter, search (ilike on 6 fields), and sort. Stable flat query keys.
// Stock 'low' uses a final client-side narrowing (column-to-column comparison
// is not expressible cleanly via PostgREST), but the result set is already
// constrained server-side to available > 0 first.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

export type InventoryItem = Database['public']['Tables']['inventory_items']['Row'];

export type StatusFilter = 'all' | 'active' | 'inactive';
export type StockFilter = 'all' | 'low' | 'out';
export type SortColumn =
  | 'name'
  | 'part_number'
  | 'category'
  | 'quantity_available'
  | 'reorder_point'
  | 'updated_at';
export type SortDir = 'asc' | 'desc';

export interface InventoryFilters {
  status: StatusFilter;
  stock: StockFilter;
  category: string | null;
  supplier: string | null;
  search: string; // already-debounced
  sortColumn: SortColumn;
  sortDir: SortDir;
}

function escapeIlike(input: string): string {
  // Escape PostgREST `or=` separator commas and SQL LIKE wildcards.
  return input.replace(/[,%_]/g, (m) => `\\${m}`);
}

export function useInventoryItems(shopId: string | undefined, filters: InventoryFilters) {
  const { status, stock, category, supplier, search, sortColumn, sortDir } = filters;

  return useQuery({
    queryKey: [
      'inventory-items',
      shopId,
      status,
      stock,
      category,
      supplier,
      search,
      sortColumn,
      sortDir,
    ] as const,
    enabled: !!shopId,
    queryFn: async (): Promise<InventoryItem[]> => {
      if (!shopId) return [];

      let query = supabase.from('inventory_items').select('*').eq('shop_id', shopId);

      // Status
      if (status === 'active') query = query.eq('is_active', true);
      else if (status === 'inactive') query = query.eq('is_active', false);

      // Stock — server-side base constraint (low handled fully after fetch)
      if (stock === 'out') {
        query = query.lte('quantity_available', 0);
      } else if (stock === 'low') {
        query = query.gt('quantity_available', 0);
      }

      // Category / supplier
      if (category) query = query.eq('category', category);
      if (supplier) query = query.eq('supplier', supplier);

      // Search — server-side ilike across 6 fields
      const trimmed = search.trim();
      if (trimmed.length > 0) {
        const pattern = `%${escapeIlike(trimmed)}%`;
        const orExpr = [
          `part_number.ilike.${pattern}`,
          `part_number_normalized.ilike.${pattern}`,
          `brand.ilike.${pattern}`,
          `name.ilike.${pattern}`,
          `supplier.ilike.${pattern}`,
          `supplier_part_number.ilike.${pattern}`,
        ].join(',');
        query = query.or(orExpr);
      }

      // Sort
      query = query.order(sortColumn, { ascending: sortDir === 'asc', nullsFirst: false });

      const { data, error } = await query;
      if (error) throw error;
      let rows = (data ?? []) as InventoryItem[];

      // Final narrowing for 'low' (PostgREST cannot compare column-to-column).
      if (stock === 'low') {
        rows = rows.filter((r) => {
          const avail = r.quantity_available ?? 0;
          return avail > 0 && avail <= (r.reorder_point ?? 0);
        });
      }

      return rows;
    },
    staleTime: 30_000,
  });
}
